/**
 * Domain types for the shared meter read estimator (SO-31411), reached through
 * the backoffice gateway's `POST /assets/meter-read/estimate`.
 *
 * The three modes an agent sees map onto two backend modes: "2 reads → read"
 * and "2 reads → annual consumption" are both TWO_READS, they just read a
 * different field off the response.
 */
export enum EstimatorMode {
  TwoReads = "TWO_READS",
  KnownReadPlusEacAq = "KNOWN_READ_PLUS_EAC_AQ",
}

export enum EstimatorFuelType {
  Electricity = "ELECTRICITY",
  Gas = "GAS",
}

/** The unit a gas register records in. Electricity registers are always kWh. */
export enum VolumeUnit {
  CubicMetres = "M3",
  HundredsCubicFeet = "HCF",
  ThousandsCubicFeet = "THCF",
  ThousandsCubicMetres = "TCM3",
}

export const VOLUME_UNIT_LABELS: Record<VolumeUnit, string> = {
  [VolumeUnit.CubicMetres]: "Cubic metres (m³)",
  [VolumeUnit.HundredsCubicFeet]: "Hundreds of cubic feet",
  [VolumeUnit.ThousandsCubicFeet]: "Thousands of cubic feet",
  [VolumeUnit.ThousandsCubicMetres]: "Thousands of cubic metres",
}

export interface IEstimateRegisterInput {
  registerName: string
  /** The most recent known read. Required in both modes. */
  read2Value: number
  read2Date: string
  /** The older read. TWO_READS only. */
  read1Value?: number
  read1Date?: string
  /** Annual consumption in kWh. KNOWN_READ_PLUS_EAC_AQ only. */
  eacAq?: number
  registerDigits?: number
  registerDecimalPlaces?: number
  unit?: VolumeUnit
}

export interface IEstimateRequest {
  mode: EstimatorMode
  fuelType: EstimatorFuelType
  seasonal: boolean
  targetDate: string
  registers: IEstimateRegisterInput[]
  calorificValue?: number
}

/**
 * How the projection fell outside the register, when it did. The two cases
 * mean opposite things and are shown differently: running past the top is an
 * ordinary rollover on a long forward projection, while going below zero says
 * the estimate is suspect rather than that the meter rolled.
 *
 * Absent when the raw read sat inside the register.
 */
export enum RegisterWrapKind {
  Overflowed = "OVERFLOWED",
  WentNegative = "WENT_NEGATIVE",
}

export interface IRegisterEstimate {
  registerName: string
  /** Raw projection: signed, unwrapped and unrounded. Not for display. */
  estimatedRead: number
  /** The read as the meter would show it, wrapped and rounded by the backend. */
  estimatedReadFormatted: number
  /** Null when the projection sat inside the register. */
  wrapped: RegisterWrapKind | null
  estimatedConsumptionKwh: number
  targetPeriodWeight: number
  /** TWO_READS only. */
  knownPeriodWeight: number | null
  /** Annual consumption implied by the two reads. TWO_READS only. */
  estimatedAqKwh: number | null
}

export interface IMeterReadEstimate {
  seasonal: boolean
  registers: IRegisterEstimate[]
  /** Non-empty when the inputs are rejected. Surface verbatim, never swallow. */
  validationErrors: string[]
  /** The calorific value the backend actually used. Gas only. */
  calorificValueApplied: number | null
}
